// import { useState, useEffect } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { omit } from 'lodash';
export const ACCESS_TOKEN_KEY = '__dex_web_token';
export const ID_TOKEN_KEY = '__dex_web_id_token';
export const EMAIL_KEY = '__dex_web_email';
export const USER_ID_KEY = '__dex_web_user_id';
export const TOKEN_GROUP_KEY = '__dex_web_token_group';
export type TokenItem = {
  accessToken: string;
  idToken: string;
  email: string;
  userId: string;
};

export type TokenStorageItem = {
  accessToken: string;
  idToken?: string;
  email?: string;
  userId?: string;
};
// export const STORAGE_KEY_NAME = '__pumpx_session_token';
export const getJwtToken = (): TokenItem | undefined => {
  try {
    const accessToken = global.localStorage.getItem(ACCESS_TOKEN_KEY);
    const idToken = global.localStorage.getItem(ID_TOKEN_KEY);
    const email = global.localStorage.getItem(EMAIL_KEY);
    const userId = global.localStorage.getItem(USER_ID_KEY);
    return {
      accessToken: accessToken ?? '',
      idToken: idToken ?? '',
      email: email ?? '',
      userId: userId ?? '',
    };
  } catch {
    return undefined;
  }
};
export const getStorageToken = (key: string): TokenStorageItem => {
  const parsedGroup: Record<string, TokenStorageItem> = JSON.parse(
    global.localStorage.getItem(TOKEN_GROUP_KEY) ?? '',
  );
  return parsedGroup[key];
};
export const hasToken = (key: string) => {
  try {
    const parsedGroup: Record<string, TokenStorageItem> = JSON.parse(
      global.localStorage.getItem(TOKEN_GROUP_KEY) ?? '',
    );
    return !!parsedGroup[key];
  } catch (e) {
    console.error(e);
    return false;
  }
};
export const clearToken = () => {
  try {
    const accessToken = global.localStorage.getItem(ACCESS_TOKEN_KEY);
    global.localStorage.removeItem(ACCESS_TOKEN_KEY);
    global.localStorage.removeItem(ID_TOKEN_KEY);
    global.localStorage.removeItem(EMAIL_KEY);
    global.localStorage.removeItem(USER_ID_KEY);
    const parsedGroup: Record<string, TokenStorageItem> = JSON.parse(
      global.localStorage.getItem(TOKEN_GROUP_KEY) ?? '',
    );
    Object.keys(parsedGroup).forEach((key: string) => {
      if (parsedGroup[key]?.accessToken === accessToken && !!accessToken) {
        const obj = omit(parsedGroup, [key]);
        global.localStorage.setItem(TOKEN_GROUP_KEY, JSON.stringify(obj));
      }
    });
  } catch {}
};
type UseSessionResult = {
  isLogin: boolean;
  accessToken?: string;
  idToken?: string;
  email?: string;
  userId?: string;
};
export const useSession = (): UseQueryResult<UseSessionResult> => {
  return useQuery({
    queryKey: ['user-session'],
    queryFn: async (): Promise<UseSessionResult> => {
      const { accessToken, idToken, email, userId } = getJwtToken() ?? {};
      return {
        isLogin: !!accessToken,
        accessToken,
        idToken,
        email,
        userId,
      };
    },
  });
};
// export const useLocalStorage = <T>(key: string, initialValue: T) => {
//   const [storedValue, setStoredValue] = useState<T>(initialValue);
//
//   useEffect(() => {
//     if (typeof window !== 'undefined') {
//       try {
//         const item = window.localStorage.getItem(key);
//         setStoredValue(item ? JSON.parse(item) : initialValue);
//       } catch (error) {
//         console.error(error);
//         setStoredValue(initialValue);
//       }
//     }
//   }, [key, initialValue]);
//
//   const setValue = (value: T | ((val: T) => T)) => {
//     if (typeof window !== 'undefined') {
//       try {
//         const valueToStore =
//           value instanceof Function ? value(storedValue) : value;
//         setStoredValue(valueToStore);
//         window.localStorage.setItem(key, JSON.stringify(valueToStore));
//       } catch (error) {
//         console.error(error);
//       }
//     }
//   };
//
//   return [storedValue, setValue] as const;
// };
export async function switchSession(key: string) {
  const token = getStorageToken(key);
  if (token) {
    global.localStorage.setItem(ACCESS_TOKEN_KEY, token.accessToken);
    global.localStorage.setItem(ID_TOKEN_KEY, token.idToken ?? '');
    global.localStorage.setItem(EMAIL_KEY, token?.email ?? '');
    global.localStorage.setItem(USER_ID_KEY, token?.userId ?? '');
  } else {
    throw new Error('no such account');
  }
}
export async function storeToGroup(key: string, tokenItem: TokenStorageItem) {
  const parsedGroup: Record<string, TokenStorageItem> = JSON.parse(
    global.localStorage.getItem(TOKEN_GROUP_KEY) ?? '{}',
  );
  parsedGroup[key] = tokenItem;
  global.localStorage.setItem(TOKEN_GROUP_KEY, JSON.stringify(parsedGroup));
}
export async function storeSession(args: {
  key: string;
  type: 'email' | 'wallet' | 'web3Login';
  idToken?: string;
  accessToken: string;
  email?: string;
  userId?: string;
  omniAccount?: string;
}): Promise<void> {
  switch (args.type) {
    case 'web3Login':
      if (args.idToken && args.accessToken && args.omniAccount) {
        // email login
        global.localStorage.setItem(ACCESS_TOKEN_KEY, args.accessToken);
        global.localStorage.setItem(ID_TOKEN_KEY, args.idToken);
        // global.localStorage.setItem(EMAIL_KEY, args.email);
        global.localStorage.setItem(USER_ID_KEY, args.omniAccount ?? '');
        await storeToGroup(args.key, {
          accessToken: args.accessToken,
          idToken: args.idToken,
          userId: args.omniAccount,
        });
        return;
      } else {
        throw new Error('Invalid login');
      }
    case 'email':
      if (args.idToken && args.accessToken && args.email && args.userId) {
        // email login
        global.localStorage.setItem(ACCESS_TOKEN_KEY, args.accessToken);
        global.localStorage.setItem(ID_TOKEN_KEY, args.idToken);
        global.localStorage.setItem(EMAIL_KEY, args.email);
        global.localStorage.setItem(USER_ID_KEY, args.userId);
        await storeToGroup(args.key, {
          accessToken: args.accessToken,
          idToken: args.idToken,
          email: args.email,
          userId: args.userId,
        });
        return;
      } else {
        throw new Error('Invalid login');
      }
    case 'wallet':
      global.localStorage.setItem(ACCESS_TOKEN_KEY, args.accessToken);
      await storeToGroup(args.key, {
        accessToken: args.accessToken,
      });
      return;
    default:
      throw new Error('Invalid login');
  }
}
